import { useState, useEffect } from "react";
import { FaSort, FaEnvelope, FaPhone, FaTimes } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmEmailModal from "./RequestModal/ConfirmEmailModal";
import ConfirmSMSModal from "./RequestModal/ConfirmSMSModal";
import RequestModal from "./RequestModal";

const SurveyRequest = () => {
  const [contacts, setContacts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestContact, setRequestContact] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isBulkSMS, setIsBulkSMS] = useState(false);
  const [isBulkEmail, setIsBulkEmail] = useState(false);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchContacts = async (page = 1) => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          `https://zola.technology/api/company/customers/get-contacts/?page=${page}&page_size=${itemsPerPage}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Fetch error");
        setContacts(data.data || []);
        setTotalPages(data.total_pages || 1);
      } catch (err) {
        setError("Failed to load contacts. Please try again.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchContacts(currentPage);
  }, [currentPage, itemsPerPage]);

  const handleSelectContact = (id) => {
    setSelectedContacts((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedContacts(
      selectedContacts.length === contacts.length
        ? []
        : contacts.map((c) => c.id)
    );
  };

  const sortData = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    const sorted = [...contacts].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setContacts(sorted);
    setSortConfig({ key, direction });
  };

  const sendBulkSMS = async () => {
    setSending(true);
    const selected = selectedContacts.length
      ? contacts.filter((c) => selectedContacts.includes(c.id))
      : requestContact
      ? [requestContact]
      : [];
    if (!selected.length)
      return toast.error("No contacts selected for SMS."), setSending(false);

    const contactDetails = selected.map((c) => ({
      phone_number: c.phone_number,
      name: c.name,
    }));
    const response = await fetch(
      "https://zola.technology/api/company/send-bulk-sms/",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sms_type: "survey",
          contact_details: contactDetails,
        }),
      }
    );

    const responseData = await response.json();
    setSending(false);
    if (!response.ok)
      return toast.error(responseData.message || "SMS send failed");
    toast.success("SMS sent successfully.");
    setShowRequestModal(false);
    setSelectedContacts([]);
  };

  const sendBulkEmails = async () => {
    setSending(true);
    const selected = selectedContacts.length
      ? contacts.filter((c) => selectedContacts.includes(c.id))
      : requestContact
      ? [requestContact]
      : [];
    if (!selected.length)
      return toast.error("No contacts selected for Email."), setSending(false);

    const contactDetails = selected.map((c) => ({
      email: c.email,
      name: c.name,
    }));
    const response = await fetch(
      "https://zola.technology/api/company/send-bulk-emails/",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email_type: "survey",
          contact_details: contactDetails,
        }),
      }
    );

    const responseData = await response.json();
    setSending(false);
    if (!response.ok)
      return toast.error(responseData.message || "Email send failed");
    toast.success("Email sent successfully.");
    setShowRequestModal(false);
    setSelectedContacts([]);
  };

  const handleRequestSurvey = (contact) => {
    setRequestContact(contact);
    setShowRequestModal(true);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  return (
    <div className="lg:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="w-full mx-auto bg-white dark:bg-gray-800 p-6 rounded-md shadow-md">
        <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-semibold dark:text-white">
              Survey Requests
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              Manage your Survey request contacts.
            </p>
          </div>
        </div>

        {selectedContacts.length > 0 && (
          <div className="bg-blue-100 dark:bg-blue-700 p-4 rounded-md mb-4 flex justify-between items-center">
            <p className="text-blue-600 dark:text-white font-medium">
              {selectedContacts.length} contacts selected
            </p>
            <div className="flex gap-4">
              <button
                className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center gap-2"
                onClick={() => {
                  setIsBulkEmail(true);
                  setShowEmailModal(true);
                }}
              >
                <FaEnvelope /> Send Email
              </button>
              <button
                className="bg-green-500 text-white px-4 py-2 rounded-md flex items-center gap-2"
                onClick={() => {
                  setIsBulkSMS(true);
                  setShowSMSModal(true);
                }}
              >
                <FaPhone /> Send SMS
              </button>
              <button
                className="bg-gray-500 text-white px-4 py-2 rounded-md flex items-center gap-2"
                onClick={() => setSelectedContacts([])}
              >
                <FaTimes /> Clear Selection
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          {loading ? (
            <p className="text-center text-gray-500 dark:text-gray-400">
              Loading...
            </p>
          ) : contacts.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-4">
              <p>No contacts available</p>
              <a
                href="/dashboard/contacts"
                className="mt-2 inline-block bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Add New Contacts
              </a>
            </div>
          ) : (
            <table className="min-w-full table-auto border-separate border-spacing-y-2">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={selectedContacts.length === contacts.length}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      Name
                      <button onClick={() => sortData("name")}>
                        {" "}
                        <FaSort />{" "}
                      </button>
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    Request Survey
                  </th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedContacts.includes(contact.id)}
                        onChange={() => handleSelectContact(contact.id)}
                      />
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {contact.name}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {contact.email || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      {contact.phone_number || "-"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700"
                        onClick={() => handleRequestSurvey(contact)}
                      >
                        Send Request
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="flex justify-between items-center mt-4">
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className={`px-4 py-2 rounded-md ${
            currentPage === 1
              ? "bg-gray-300 text-gray-600"
              : "bg-blue-500 text-white"
          }`}
        >
          Previous
        </button>
        <span className="text-gray-700 dark:text-gray-300">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 rounded-md ${
            currentPage === totalPages
              ? "bg-gray-300 text-gray-600"
              : "bg-blue-500 text-white"
          }`}
        >
          Next
        </button>
      </div>

      <RequestModal
        contact={requestContact}
        onClose={() => {
          setShowRequestModal(false);
          setRequestContact(null);
        }}
        onChooseSMS={() => {
          setShowRequestModal(false);
          setShowSMSModal(true);
        }}
        onChooseEmail={() => {
          setShowRequestModal(false);
          setShowEmailModal(true);
        }}
      />

      {showSMSModal && (
        <ConfirmSMSModal
          contact={requestContact}
          isBulk={isBulkSMS}
          messageType="survey"
          onConfirm={() => {
            sendBulkSMS();
            setShowSMSModal(false);
            setIsBulkSMS(false);
          }}
          onCancel={() => {
            setShowSMSModal(false);
            setIsBulkSMS(false);
          }}
        />
      )}

      {showEmailModal && (
        <ConfirmEmailModal
          contact={requestContact}
          isBulk={isBulkEmail}
          messageType="survey"
          onConfirm={() => {
            sendBulkEmails();
            setShowEmailModal(false);
            setIsBulkEmail(false);
          }}
          onCancel={() => {
            setShowEmailModal(false);
            setIsBulkEmail(false);
          }}
        />
      )}
    </div>
  );
};

export default SurveyRequest;
